import { create } from 'zustand';

export interface WindowState {
  id: string;
  appId: string;
  title: string;
  isMinimized: boolean;
  isMaximized: boolean;
  zIndex: number;
  x: number;
  y: number;
  width: number;
  height: number;
}

interface WindowStore {
  windows: WindowState[];
  nextZIndex: number;
  openWindow: (appId: string, title: string, defaultWidth?: number, defaultHeight?: number) => void;
  closeWindow: (id: string) => void;
  minimizeWindow: (id: string) => void;
  maximizeWindow: (id: string) => void;
  focusWindow: (id: string) => void;
  restoreWindow: (id: string) => void;
  moveWindow: (id: string, x: number, y: number) => void;
  resizeWindow: (id: string, width: number, height: number, x?: number, y?: number) => void;
  getActiveWindow: () => WindowState | null;
  isWindowOpen: (appId: string) => boolean;
}

const MAX_WINDOWS = 10;
const CASCADE_OFFSET = 28;

export const useWindowStore = create<WindowStore>((set, get) => ({
  windows: [],
  nextZIndex: 100,

  openWindow: (appId, title, defaultWidth = 900, defaultHeight = 600) => {
    const { windows, nextZIndex } = get();

    const existingWindow = windows.find(w => w.appId === appId);
    if (existingWindow) {
      get().focusWindow(existingWindow.id);
      if (existingWindow.isMinimized) get().restoreWindow(existingWindow.id);
      return;
    }

    if (windows.length >= MAX_WINDOWS) {
      console.warn('Maximum number of windows (10) reached');
      return;
    }

    const cascadeIndex = windows.filter(w => !w.isMinimized).length;
    const vw = typeof window !== 'undefined' ? window.innerWidth  : 1200;
    const vh = typeof window !== 'undefined' ? window.innerHeight : 800;
    const baseX = Math.max(40, (vw - defaultWidth)  / 2 + cascadeIndex * CASCADE_OFFSET);
    const baseY = Math.max(20, (vh - defaultHeight) / 2 + cascadeIndex * CASCADE_OFFSET - 20);
    const x = Math.min(baseX, vw - 200);
    const y = Math.min(baseY, vh - 200);

    const newWindow: WindowState = {
      id: `window-${Date.now()}-${Math.random()}`,
      appId,
      title,
      isMinimized: false,
      isMaximized: false,
      zIndex: nextZIndex,
      x,
      y,
      width: defaultWidth,
      height: defaultHeight,
    };

    set({ windows: [...windows, newWindow], nextZIndex: nextZIndex + 1 });
  },

  closeWindow: (id) => {
    set(state => ({ windows: state.windows.filter(w => w.id !== id) }));
  },

  minimizeWindow: (id) => {
    set(state => ({
      windows: state.windows.map(w => w.id === id ? { ...w, isMinimized: true } : w),
    }));
  },

  maximizeWindow: (id) => {
    set(state => ({
      windows: state.windows.map(w => w.id === id ? { ...w, isMaximized: !w.isMaximized } : w),
    }));
  },

  focusWindow: (id) => {
    const { nextZIndex } = get();
    set(state => ({
      windows: state.windows.map(w => w.id === id ? { ...w, zIndex: nextZIndex } : w),
      nextZIndex: nextZIndex + 1,
    }));
  },

  restoreWindow: (id) => {
    const { nextZIndex } = get();
    set(state => ({
      windows: state.windows.map(w =>
        w.id === id ? { ...w, isMinimized: false, zIndex: nextZIndex } : w
      ),
      nextZIndex: nextZIndex + 1,
    }));
  },

  moveWindow: (id, x, y) => {
    set(state => ({
      windows: state.windows.map(w => w.id === id ? { ...w, x, y } : w),
    }));
  },

  resizeWindow: (id, width, height, x, y) => {
    set(state => ({
      windows: state.windows.map(w =>
        w.id === id
          ? { ...w, width, height, ...(x !== undefined ? { x } : {}), ...(y !== undefined ? { y } : {}) }
          : w
      ),
    }));
  },

  getActiveWindow: () => {
    const { windows } = get();
    if (windows.length === 0) return null;
    return windows.reduce((highest, current) =>
      current.zIndex > highest.zIndex ? current : highest
    );
  },

  isWindowOpen: (appId) => {
    return get().windows.some(w => w.appId === appId);
  },
}));
